import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu } from 'primereact/menu';
import { Button } from '../../../../components/ui/Button';
import { useIsRestaurant } from '../../../../hooks/useAccountType';

/**
 * Restaurant-only sub-navigation for the Video Wall section. Surfaces the
 * features that used to be buried in the "… More" dropdown as a route-based tab
 * bar so a non-technical restaurant owner can actually find them.
 *
 * PRIMARY pills (the 4 daily-use destinations) + a "…" overflow for the less-used
 * Locations / Simulator / Setup Guide. The active tab is derived from the current
 * route (these are real routes, not local tab state — deep-linkable + back-button
 * friendly). Renders nothing for standard accounts, so their layout is unchanged.
 */
const PRIMARY = [
  { key: 'walls',     labelKey: 'nav.vw_my_walls',         to: '/apps/video-wall/walls',     icon: 'pi pi-th-large' },
  { key: 'groups',    labelKey: 'video_wall.groups.title', to: '/apps/video-wall/groups',    icon: 'pi pi-object-group' },
  { key: 'menus',     labelKey: 'video_wall.menus.title',  to: '/apps/video-wall/menus',     icon: 'pi pi-list' },
  { key: 'templates', labelKey: 'video_wall.templates.title', to: '/apps/video-wall/templates', icon: 'pi pi-palette' },
];

const SECONDARY = [
  { labelKey: 'video_wall.locations.title', to: '/apps/video-wall/locations', icon: 'pi pi-map-marker' },
  { labelKey: 'video_wall.simulator.title', to: '/apps/video-wall/simulator', icon: 'pi pi-desktop' },
  { labelKey: 'video_wall.setup_guide',     to: '/apps/video-wall/create',    icon: 'pi pi-compass' },
];

export default function WallTabs() {
  const isRestaurant = useIsRestaurant();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const moreRef = useRef(null);

  if (!isRestaurant) return null;

  const activeKey = PRIMARY.find((tab) => pathname.startsWith(tab.to))?.key || null;
  const secondaryActive = SECONDARY.some((s) => pathname.startsWith(s.to));

  const moreModel = SECONDARY.map((s) => ({
    label: t(s.labelKey), icon: s.icon, command: () => navigate(s.to),
  }));

  return (
    <div className="d-flex align-items-center gap-2 mb-3"
      style={{ borderBottom: '1px solid var(--pp-border)', paddingBottom: 8, overflowX: 'auto' }}>
      {PRIMARY.map((tab) => (
        <Button key={tab.key} size="small" icon={tab.icon} label={t(tab.labelKey)}
          outlined={activeKey !== tab.key} onClick={() => navigate(tab.to)}
          style={{ flex: 'none' }} />
      ))}
      <Button size="small" icon="pi pi-ellipsis-h" aria-haspopup aria-label={t('video_wall.more')}
        outlined={!secondaryActive} onClick={(e) => moreRef.current?.toggle(e)}
        style={{ flex: 'none' }} />
      <Menu model={moreModel} popup ref={moreRef} />
    </div>
  );
}
